import { useEffect, useRef, useCallback, Fragment } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { T } from "../../styles/GlobalStyles";

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Accessible modal wrapper — Escape to close, focus trap, ARIA roles.
 * Wraps any modal content with backdrop, animation, and keyboard handling.
 *
 * @param {boolean} open        visibility toggle
 * @param {function} onClose    called on backdrop click or Escape
 * @param {string} [titleId]    id of the element labelling the modal (for aria-labelledby)
 * @param {string} [size]       "sm" | "md" | "lg" — max-width shortcut
 * @param {object} [style]      extra styles on the panel
 * @param {React.ReactNode} children
 */
const ModalWrapper = ({ open, onClose, titleId, size = "md", style, children }) => {
  const panelRef = useRef(null);
  const triggerRef = useRef(null);

  const maxW = size === "sm" ? 400 : size === "lg" ? 700 : 520;

  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement;
      requestAnimationFrame(() => {
        const first = panelRef.current?.querySelector(FOCUSABLE);
        first?.focus();
      });
    } else if (triggerRef.current) {
      triggerRef.current.focus();
      triggerRef.current = null;
    }
  }, [open]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }

      if (e.key !== "Tab") return;

      const focusables = panelRef.current?.querySelectorAll(FOCUSABLE);
      if (!focusables?.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  return createPortal(
    <AnimatePresence>
      {open ? (
        <Fragment key="modal-root">
          <motion.div
            key="mw-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            aria-hidden="true"
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 100000,
              background: "rgba(0,0,0,0.7)",
            }}
          />
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 100001,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
          <motion.div
            key="mw-panel"
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="hide-scrollbar"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
            style={{
              pointerEvents: "auto",
              zIndex: 100001,
              background: T.surface,
              borderRadius: 16,
              maxWidth: maxW,
              width: `min(${maxW}px, calc(100vw - 32px))`,
              maxHeight: "85vh",
              minHeight: 0,
              overflowX: "hidden",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              padding: 28,
              color: T.text,
              outline: "none",
              boxShadow: "0 8px 32px rgba(0,0,0,0.28)",
              ...style,
            }}
          >
            {children}
          </motion.div>
          </div>
        </Fragment>
      ) : null}
    </AnimatePresence>,
    document.body
  );
};

export default ModalWrapper;
